import React, {
  useContext,
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { classNames } from 'primereact/utils';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { MultiSelect } from 'primereact/multiselect';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { AuthContext } from '../contexts/AuthContext';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import api from '../api/api';
import '../layout/sass/_form.scss';
import { ErrorValidationMessage } from './ErrorValidationMessage';
import { useHistory } from 'react-router-dom';
import moment from 'moment';

export const FormPlanejamento = ({
  isEditing = false,
  isLoading = false,
  defaultValues = {},
}) => {
  const toast = useRef(null);
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const isMounted = useRef(true);

  // Options
  const [instrutoresOptions, setInstrutoresOptions] = useState([]);
  // const [cursosOptions, setCursosOptions] = useState([]);
  const [tarefasOptions, setTarefasOptions] = useState([]);
  // const [allCursosOptions, setAllCursosOptions] = useState([]);
  const [allTurmas, setAllTurmas] = useState([]);
  const [turmaPlanejamento, setTurmaPlanejamento] = useState([]);

  // Selected tasks in the form
  const [selectedTarefas, setSelectedTarefas] = useState([]);

  // Simplified add task controls
  const [taskSelectId, setTaskSelectId] = useState(null);
  const [taskDate, setTaskDate] = useState(null);
  const [taskPeriod, setTaskPeriod] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // New Task dialog
  const [showNewTaskDialog, setShowNewTaskDialog] = useState(false);
  const [newTaskDescricao, setNewTaskDescricao] = useState('');
  const [newTaskObs, setNewTaskObs] = useState('');
  const [newTaskPublica, setNewTaskPublica] = useState(false);
  const [newTaskAtiva, setNewTaskAtiva] = useState(true);
  const [redirectAfterToast, setRedirectAfterToast] = useState(false);

  const lastFetchedInstrutor = useRef(null);
  const turmasInicializadas = useRef(false);

  const periodoLetivoOptions = [
    { label: '1º Bimestre', value: 'B_01' },
    { label: '2º Bimestre', value: 'B_02' },
    { label: '3º Bimestre', value: 'B_03' },
    { label: '4º Bimestre', value: 'B_04' },
    { label: '1º Trimestre', value: 'T_01' },
    { label: '2º Trimestre', value: 'T_02' },
    { label: '3º Trimestre', value: 'T_03' },
  ];

  const DIA_SEMANA_ORDER = {
    SEGUNDA: 1,
    TERCA: 2,
    QUARTA: 3,
    QUINTA: 4,
    SEXTA: 5,
    SABADO: 6,
    DOMINGO: 7,
  };

  const stableDefaultValues = useMemo(
    () => defaultValues || {},
    [defaultValues]
  );
  const userEmail = user?.email;
  const isInstrutor = user?.perfilUser.includes('user-instrutor');

  // Formik
  const planejamentoSchema = Yup.object({
    descricao: Yup.string()
      .required('Descrição obrigatória')
      .max(2048, 'Máximo de 2048 caracteres'),

    vigencia: Yup.date().nullable().required('Vigência obrigatória'),

    instrutorResponsavel: Yup.number()
      .typeError('Instrutor obrigatório')
      .required('Instrutor obrigatório'),

    turmas: Yup.array().of(Yup.number()).nullable(),
  });

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      id: stableDefaultValues.id || null,
      descricao: stableDefaultValues.descricao || '',
      vigencia: stableDefaultValues.vigencia
        ? moment(stableDefaultValues.vigencia, 'YYYY-MM-DD').toDate()
        : null,
      instrutorResponsavel:
        stableDefaultValues.instrutorResponsavel?.id || null,
      turmas: Array.isArray(stableDefaultValues.turmas)
        ? stableDefaultValues.turmas.map((c) => c.id)
        : [],
    },

    validationSchema: planejamentoSchema,

    onSubmit: async (values) => {
      setIsSubmitting(true);

      const hasCurso = values.turmas?.length > 0;
      const hasTurma = !!values.turma;

      if (!hasCurso && !hasTurma) {
        toast.current.show({
          severity: 'error',
          summary: 'Erro',
          detail: 'Informe curso ou turma',
          life: 4000,
        });
        return;
      }

      const payload = {
        id: values.id || null,
        descricao: values.descricao,
        vigencia: values.vigencia
          ? moment(values.vigencia).format('YYYY-MM-DD')
          : null,
        instrutorResponsavel: {
          id: values.instrutorResponsavel,
        },
        turmas: values.turmas.map((id) => ({ id })),
        tarefas: (selectedTarefas || []).map((t) => ({
          tarefa: { id: t.tarefa.id },
          dataLimite: t.dataLimite
            ? moment(t.dataLimite).format('YYYY-MM-DD')
            : null,
          periodoLetivo: t.periodoLetivo || null,
        })),
      };

      try {
        if (isEditing) {
          await api.put('/planejamento', payload);
          setRedirectAfterToast(true);
          toast.current.show({
            severity: 'success',
            summary: 'Atualizado',
            detail: 'Planejamento atualizado',
            life: 2000,
          });
        } else {
          await api.post('/planejamento', payload);
          setRedirectAfterToast(true);
          toast.current.show({
            severity: 'success',
            summary: 'Criado',
            detail: 'Planejamento criado',
            life: 2000,
          });
        }
      } catch (err) {
        toast.current.show({
          severity: 'error',
          summary: 'Erro',
          detail: 'Falha ao salvar planejamento',
          life: 3000,
        });
      } finally {
        if (isMounted.current) {
          setIsSubmitting(false);
        }
      }
    },
  });

  const isFormFieldValid = (name) =>
    !!(formik.touched[name] && formik.errors[name]);

  const getTurmasInstrutor = useCallback(
    async (idInstrutor) => {
      if (!idInstrutor) return;

      try {
        const response = await api.get(
          `turma/turmaAllInstrutor/id/${idInstrutor}`
        );

        if (response?.data) setTurmaPlanejamento(response.data);

        const planejamentoIdAtual = stableDefaultValues?.id;

        const turmasNormalizadas = sortTurmasByDiaHora(
          (response.data || [])
            .map((turma) => {
              const vinculadaOutroPlanejamento =
                turma.planejamentoId &&
                turma.planejamentoId !== planejamentoIdAtual;

              const vinculadaPlanejamentoAtual =
                turma.planejamentoId === planejamentoIdAtual;

              return {
                ...turma,
                value: turma.id,
                label: `${turma.cursoNome} - ${turma.diaSemana
                  } ${turma.hora.slice(0, 5)}`,
                disabled: vinculadaOutroPlanejamento,
                bloqueada: vinculadaPlanejamentoAtual,
              };
            })
            .filter((turma) => {
              if (!isEditing) {
                return !turma.planejamentoId;
              }

              return (
                !turma.planejamentoId ||
                turma.planejamentoId === planejamentoIdAtual
              );
            })
        );

        setAllTurmas(turmasNormalizadas);
      } catch (error) {
        toast.current?.show({
          severity: 'error',
          summary: 'Erro ao obter turmas',
          detail: error.toString(),
        });
      }
    },
    [isEditing, stableDefaultValues?.id]
  );

  useEffect(() => {
    const controller = new AbortController();
    isMounted.current = true;

    const fetchAll = async () => {
      if (!userEmail) return;

      setLoadingOptions(true);

      try {
        const [instrResp, tarefasResp] = await Promise.all([
          api.get('/perfil/instrutor', { signal: controller.signal }),
          api.get('/tarefa/tarefaAllByInstrutor', {
            params: { email: userEmail },
            signal: controller.signal
          })
        ]);

        if (!isMounted.current) return;

        const userRes = await api.get(`perfil/email/${user.email}`);

        /* ---------- Instrutores ---------- */
        setInstrutoresOptions(
          (instrResp.data || []).map((i) => ({
            label: i.nome,
            value: i.id,
          }))
        );

        /* ---------- Tarefas ---------- */
        setTarefasOptions(
          (tarefasResp.data || []).map((t) => ({
            label: t.descricao,
            value: t.id,
          }))
        );

        /* ---------- Instrutor logado ---------- */
        if (isInstrutor && userRes?.data?.id) {
          formik.setFieldValue('instrutorResponsavel', userRes.data.id);
          await getTurmasInstrutor(userRes.data.id);
        }

        if (isEditing && stableDefaultValues) {
          if (Array.isArray(stableDefaultValues.turmas)) {
            formik.setFieldValue(
              'turmas',
              stableDefaultValues.turmas
                .map((t) => t?.id)
                .filter((id) => id != null)
            );
          }

          if (Array.isArray(stableDefaultValues.tarefas)) {
            const mapped = stableDefaultValues.tarefas.map((pt) => ({
              tarefa: {
                id: pt.tarefa?.id,
                label: pt.tarefa?.descricao || '—',
              },
              dataLimite: pt.dataLimite ? new Date(pt.dataLimite) : null,
              periodoLetivo: pt.periodoLetivo || null,
            }));

            setSelectedTarefas(mapped);
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Erro obtendo opções', err);
          toast.current?.show({
            severity: 'warn',
            summary: 'Aviso',
            detail: 'Erro ao carregar opções',
            life: 2000,
          });
        }
      } finally {
        if (isMounted.current) {
          setLoadingOptions(false);
        }
      }
    };

    fetchAll();

    return () => {
      isMounted.current = false;
      controller.abort();
    };
  }, [userEmail, isEditing, stableDefaultValues]);

  useEffect(() => {
    if (
      !isEditing ||
      !stableDefaultValues?.instrutorResponsavel ||
      !Array.isArray(stableDefaultValues?.turmas)
    ) {
      return;
    }

    turmasInicializadas.current = true;

    const idInstrutor = stableDefaultValues.instrutorResponsavel?.id;

    const carregarEdicao = async () => {
      await getTurmasInstrutor(idInstrutor);

      formik.setFieldValue(
        'turmas',
        stableDefaultValues.turmas.map((t) => t?.id).filter((id) => id != null)
      );

      lastFetchedInstrutor.current = idInstrutor;
    };

    carregarEdicao();
  }, [isEditing, stableDefaultValues, getTurmasInstrutor]);

  const normalizeDiaSemana = (dia) =>
    dia
      ?.normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();

  const sortTurmasByDiaHora = (list) =>
    [...list].sort((a, b) => {
      const diaAKey = normalizeDiaSemana(a.diaSemana);
      const diaBKey = normalizeDiaSemana(b.diaSemana);

      const diaA = DIA_SEMANA_ORDER[diaAKey] ?? 99;
      const diaB = DIA_SEMANA_ORDER[diaBKey] ?? 99;

      if (diaA !== diaB) return diaA - diaB;

      return a.hora.localeCompare(b.hora);
    });

  // Add/remove tarefas
  const handleAddTask = async (e) => {
    e?.preventDefault();
    if (!taskSelectId || !taskDate || !taskPeriod) return;

    const jaExistePlanejamento = turmaPlanejamento?.some(
      (item) => item.planejamentoId !== null
    );

    if (!isEditing) {
      if (jaExistePlanejamento) {
        toast.current.show({
          severity: 'error',
          summary: 'Erro',
          detail: 'Uma das turmas já possui um planejamento!',
          life: 5000,
        });
        return;
      }
    }

    const tarefaObj = {
      tarefa: {
        id: taskSelectId,
        label:
          tarefasOptions.find((t) => t.value === taskSelectId)?.label ||
          `Tarefa ${taskSelectId}`,
      },
      dataLimite: taskDate,
      periodoLetivo: taskPeriod,
    };
    setSelectedTarefas((prev) => {
      const filtered = (prev || []).filter(
        (p) => p.tarefa.id !== tarefaObj.tarefa.id
      );
      return [...filtered, tarefaObj];
    });
    setTaskSelectId(null);
    setTaskDate(null);
    setTaskPeriod(null);
  };

  const handleRemoveTask = (idx) => {
    setSelectedTarefas((prev) => prev.filter((_, i) => i !== idx));
  };

  // Create new tarefa
  const handleCreateTask = async () => {
    if (!newTaskDescricao.trim()) {
      toast.current.show({
        severity: 'warn',
        summary: 'Aviso',
        detail: 'Informe a descrição da tarefa',
        life: 2000,
      });
      return;
    }

    try {

      const response = await api.post('/tarefa', {
        descricao: newTaskDescricao,
        obs: newTaskObs,
        ativa: newTaskAtiva,
        perfilEmail: newTaskPublica ? null : user.email

      });
      const nova = response.data;
      const newOption = { label: nova.descricao, value: nova.id };

      setTarefasOptions((prev) => [...prev, newOption]);
      setTaskSelectId(nova.id);

      toast.current.show({
        severity: 'success',
        summary: 'Criada',
        detail: 'Tarefa criada com sucesso',
        life: 2000,
      });

      setShowNewTaskDialog(false);
      setNewTaskDescricao('');
      setNewTaskObs('');
    } catch (err) {
      console.error('Erro ao criar tarefa:', err);
      toast.current.show({
        severity: 'error',
        summary: 'Erro',
        detail: 'Falha ao criar tarefa',
        life: 2000,
      });
    }
  };

  const handleToastHide = () => {
    if (redirectAfterToast && isMounted.current) {
      setRedirectAfterToast(false);
      history.push('/planejamentos');
    }
  };

  const editTaskBodyTemplate = (rowData, { rowIndex }) => {
    return (
      <Button
        type="button"
        icon="pi pi-pencil"
        className="p-button-rounded p-button-info p-button-text"
        tooltip="Editar esta tarefa"
        onClick={() => {
          setTaskSelectId(rowData.tarefa.id);
          setTaskDate(rowData.dataLimite ? new Date(rowData.dataLimite) : null);
          setTaskPeriod(rowData.periodoLetivo);

          handleRemoveTask(rowIndex);
        }}
      />
    );
  };

  // Render
  return (
    <div className="card p-grid p-col-12 p-mx-0 p-mt-0">
      <Toast ref={toast} position="top-right" onHide={handleToastHide} />
      <div className="p-col-12">
        <h2 className="p-mt-0">
          {isEditing ? 'Editar Planejamento' : 'Novo Planejamento'}
        </h2>

        <form onSubmit={formik.handleSubmit}>
          <div className="p-formgrid p-grid p-fluid p-mx-0">
            {/* Descrição */}
            <div className="p-field p-col-12 p-sm-6">
              <label
                htmlFor="descricao"
                className={classNames({
                  'p-error': isFormFieldValid('descricao'),
                })}
              >
                Descrição
              </label>
              {!isLoading ? (
                <InputTextarea
                  id="descricao"
                  name="descricao"
                  value={formik.values.descricao}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  rows={4}
                  cols={40}
                  className={classNames({
                    'p-invalid': isFormFieldValid('descricao'),
                  })}
                />
              ) : (
                <Skeleton height="80px" />
              )}
              {isFormFieldValid('descricao') && (
                <ErrorValidationMessage message={formik.errors.descricao} />
              )}
            </div>

            <div className="p-w-100 p-col-12 p-sm-6">
              {/* Vigência */}
              <div className="p-field p-col-12 p-sm-12">
                <label
                  htmlFor="vigencia"
                  className={classNames({
                    'p-error': isFormFieldValid('vigencia'),
                  })}
                >
                  Vigência
                </label>

                {!isLoading ? (
                  <Calendar
                    id="vigencia"
                    name="vigencia"
                    value={formik.values.vigencia}
                    onChange={(e) => formik.setFieldValue('vigencia', e.value)}
                    dateFormat="dd/mm/yy"
                    showIcon
                    className={classNames({
                      'p-invalid': isFormFieldValid('vigencia'),
                    })}
                  />
                ) : (
                  <Skeleton height="35px" />
                )}

                {isFormFieldValid('vigencia') && (
                  <ErrorValidationMessage message={formik.errors.vigencia} />
                )}
              </div>
            </div>

            {/* Instrutor */}
            <div className="p-field p-col-12 p-sm-6">
              <label
                htmlFor="instrutorResponsavel"
                className={classNames({
                  'p-error': isFormFieldValid('instrutorResponsavel'),
                })}
              >
                Instrutor Responsável
              </label>
              <Dropdown
                id="instrutorResponsavel"
                value={formik.values.instrutorResponsavel}
                options={instrutoresOptions}
                optionLabel="label"
                optionValue="value"
                placeholder="Selecione um instrutor"
                disabled={isInstrutor ?? loadingOptions}
                onChange={(e) => {
                  formik.setFieldValue('instrutorResponsavel', e.value);
                }}
                onBlur={() => {
                  if (isEditing) return;
                  const idInstrutor = formik.values.instrutorResponsavel;
                  if (!idInstrutor) return;
                  if (lastFetchedInstrutor.current === idInstrutor) return;
                  lastFetchedInstrutor.current = idInstrutor;

                  getTurmasInstrutor(idInstrutor);
                }}
              />
              {isFormFieldValid('instrutorResponsavel') && (
                <ErrorValidationMessage
                  message={formik.errors.instrutorResponsavel}
                />
              )}
            </div>

            {/* Cursos */}
            <div className="p-field p-col-12 p-sm-6">
              <label
                htmlFor="turmas"
                className={classNames({
                  'p-error': isFormFieldValid('turmas'),
                })}
              >
                Selecione a turma
              </label>
              <MultiSelect
                id="turmas"
                value={formik.values.turmas}
                options={allTurmas}
                onChange={(e) => formik.setFieldValue('turmas', e.value)}
                optionLabel="label"
                optionValue="value"
                placeholder="Selecione a(s) turma(s)"
                disabled={loadingOptions}
                // optionDisabled={(option) => {
                //   return stableDefaultValues?.turmas?.some(
                //     (turma) => turma.id === option.value
                //   );
                // }}
              />
              {isFormFieldValid('turmas') && (
                <ErrorValidationMessage message={formik.errors.turmas} />
              )}
            </div>

            {/* Adição de tarefa com botão nova */}
            <div className="p-field p-col-12 p-formgrid p-grid p-mb-3">
              <div className="p-field p-col-5">
                <label>Tarefa</label>
                <div className="p-d-flex p-ai-center">
                  <Dropdown
                    value={taskSelectId}
                    options={tarefasOptions}
                    onChange={(e) => setTaskSelectId(e.value)}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Selecione tarefa"
                    disabled={loadingOptions}
                    style={{ width: '95%' }}
                    className="p-mr-2"
                  />
                  <Button
                    icon="pi pi-plus"
                    className="p-button-rounded p-button-success p-button-outlined"
                    tooltip="Nova Tarefa"
                    onClick={() => setShowNewTaskDialog(true)}
                    type="button"
                  />
                </div>
              </div>

              <div className="p-field p-col-3">
                <label>Data Limite</label>
                <Calendar
                  value={taskDate}
                  onChange={(e) => setTaskDate(e.value)}
                  dateFormat="dd/mm/yy"
                  showIcon
                />
              </div>

              <div className="p-field p-col-3">
                <label>Período Letivo</label>
                <Dropdown
                  value={taskPeriod}
                  options={periodoLetivoOptions}
                  onChange={(e) => setTaskPeriod(e.value)}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Período"
                />
              </div>

              <div className="p-field p-col-1 p-d-flex p-ai-end p-jc-end">
                <Button
                  type="button"
                  label="Inserir"
                  icon="pi pi-plus"
                  onClick={(e) => handleAddTask(e)}
                  disabled={!taskSelectId || !taskDate || !taskPeriod}
                />
              </div>
            </div>

            {/* Tarefas selecionadas */}
            {selectedTarefas.length > 0 && (
              <div className="p-field p-col-12">
                <h3>Tarefas Selecionadas</h3>
                <DataTable
                  value={selectedTarefas}
                  dataKey="tarefa.id"
                  responsiveLayout="scroll"
                >
                  <Column field="tarefa.label" header="Tarefa" />
                  <Column
                    body={editTaskBodyTemplate}
                    headerStyle={{ width: '4rem' }}
                    bodyStyle={{ textAlign: 'center' }}
                  />
                  <Column
                    header="Data Limite"
                    body={(row) =>
                      row.dataLimite
                        ? moment(row.dataLimite).format('DD/MM/YYYY')
                        : '-'
                    }
                  />
                  <Column
                    header="Período Letivo"
                    body={(row) =>
                      periodoLetivoOptions.find(
                        (o) => o.value === row.periodoLetivo
                      )?.label || '-'
                    }
                  />
                  <Column
                    body={(row, { rowIndex }) => (
                      <Button
                        icon="pi pi-times"
                        className="p-button-rounded p-button-danger"
                        onClick={() => handleRemoveTask(rowIndex)}
                      />
                    )}
                    headerStyle={{ width: '5rem' }}
                  />
                </DataTable>
              </div>
            )}

            <div className="p-d-flex p-jc-end p-mr-1 p-col-12">
              <Button
                label={isEditing ? 'Atualizar' : 'Salvar'}
                type="submit"
                iconPos="right"
                loading={isSubmitting}
                disabled={isSubmitting || loadingOptions}
              />
            </div>
          </div>
        </form>
      </div>

      {/* Dialog Nova Tarefa */}
      <Dialog
        header="Nova Tarefa"
        visible={showNewTaskDialog}
        style={{ width: '30vw' }}
        modal
        onHide={() => setShowNewTaskDialog(false)}
      >
        <div className="p-fluid">
          <div className="p-field">
            <label htmlFor="descricao">Descrição</label>
            <InputText
              id="descricao"
              value={newTaskDescricao}
              onChange={(e) => setNewTaskDescricao(e.target.value)}
            />
          </div>

          <div className="p-field-checkbox" style={{ display: 'flex', gap: '1rem' }}>

            <div className="p-field-checkbox">
              <Checkbox
                inputId="Publica"
                checked={newTaskPublica}
                onChange={(e) => setNewTaskPublica(e.checked)}
              />
              <label htmlFor="Publica">Pública</label>
            </div>

            <div className="p-field-checkbox">
              <Checkbox
                inputId="ativa"
                checked={newTaskAtiva}
                onChange={(e) => setNewTaskAtiva(e.checked)}
              />
              <label htmlFor="ativa">Ativa</label>
            </div>

          </div>



          <div className="p-field">
            <label htmlFor="obs">Observações</label>
            <InputTextarea
              id="obs"
              value={newTaskObs}
              onChange={(e) => setNewTaskObs(e.target.value)}
              rows={3}
            />
          </div>



          <div className="p-d-flex p-jc-end">
            <Button
              label="Salvar"
              icon="pi pi-check"
              type="button"
              onClick={handleCreateTask}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default FormPlanejamento;
